import { useCallback, useState } from 'react';

import { useGroupRestoreCourseAccessMutation } from 'core/graphql';

/** Outcome of a repair attempt — сколько занятий реально вернулось. */
export interface IRestoreCourseAccessResult {
  success: boolean;
  /** Число занятий, к которым доступ появился; 0 — возвращать было нечего. */
  restoredLessons: number;
}

interface IUseRestoreCourseAccess {
  /**
   * Возвращает студенту доступ к занятиям его договора. Аддитивна: отнять
   * ничего не может, поэтому безопасна к повторному нажатию — второй раз
   * вернёт `restoredLessons: 0`. `onSuccess` дёргается только при успехе,
   * чтобы вызывающий обновил ростер.
   */
  restore: (agreementId: string) => Promise<IRestoreCourseAccessResult>;
  /** `true`, пока запрос в полёте. */
  pending: boolean;
}

/**
 * Ремонт доступа к курсу (кнопка в строке ростера группы). Обёртка над
 * сгенерированным `useGroupRestoreCourseAccessMutation`: мутация трогает
 * только строки посещаемости и пишет запись в аудит — договор, взносы и
 * статус расторжения остаются как есть.
 */
export const useRestoreCourseAccess = (
  onSuccess?: () => void,
): IUseRestoreCourseAccess => {
  const [run] = useGroupRestoreCourseAccessMutation();
  const [pending, setPending] = useState(false);

  const restore = useCallback(
    async (agreementId: string): Promise<IRestoreCourseAccessResult> => {
      if (pending) return { success: false, restoredLessons: 0 };
      setPending(true);

      try {
        const result = await run({ variables: { agreementId } });
        const payload = result.data?.restoreCourseAccess;
        const success = Boolean(payload?.success);
        if (success) onSuccess?.();
        return {
          success,
          restoredLessons: payload?.restoredLessons ?? 0,
        };
      } catch {
        return { success: false, restoredLessons: 0 };
      } finally {
        setPending(false);
      }
    },
    [pending, run, onSuccess],
  );

  return { restore, pending };
};
